import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosError, AxiosResponse } from "axios";
import { RootState } from "./store";
import { axiosApi, getResponseError, handleErrorRTKAndAxiosAsync } from "@/helpers";
import { ENDPOINTS_SPR, URL_SPR } from "@/constants/api_endpoints";
import {
	IAsyncModeResultDialog,
	IAsyncModeResultFailed,
	IAsyncModeResultMonolog,
	IAsyncModeStatus,
	IRecognizeSpeechProps,
	IResponseAudioPeaks,
	IResponseSuccessSync,
	IResponseTaskId,
	ISpeakerModified,
	ISprState,
	QueueResponseType,
} from "@/types/sprTypes";
import { IResponse, RequestStatus, ResponseStatus } from "@/types/statusTypes";

const initialState: ISprState = {
	recognition: {
		data: null,
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
		audioUrl: null,
		timestamp: 0,
		asyncMode: false,
	},
	asyncMode: {
		data: null,
		timestamp: 0,
		speakerList: [],
		taskId: null,
		requestStatus: RequestStatus.IDLE,
		responseStatus: null,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	deletionTask: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	queue: {
		data: null,
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	audio: {
		audioUrl: null,
		audioStatus: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
		audioPeakStatus: RequestStatus.IDLE,
		audioPeaks: [],
	},
};

// распознавание речи
export const recognizeSpeech = createAsyncThunk(
	"spr/recognize",
	async (
		{ modelName, formData, speakers, asyncMode, punctuation, vadType, speakerCounter, toxicity, voiceAnalyzer, emotion, normalization }: IRecognizeSpeechProps,
		{ rejectWithValue }
	) => {
		try {
			const response: AxiosResponse<IResponseSuccessSync | IResponse | IResponseTaskId> = await axiosApi.post(
				`${URL_SPR}/${ENDPOINTS_SPR.RECOGNITION}/${modelName}`,
				{
					wav: formData.get("file"),
					async: asyncMode,
					normalization,
					punctuation,
					vad: vadType,
					speakers,
					speaker_counter: speakerCounter,
					toxicity,
					voice_analyzer: voiceAnalyzer,
					emotion,
				},
				{
					headers: {
						"Content-Type": "multipart/form-data",
					},
				}
			);
			return response.data;
		} catch (error) {
			const handledError = await handleErrorRTKAndAxiosAsync(error);
			return rejectWithValue(handledError);
		}
	}
);

// очередь асинхронного распознавания
export const getQueue = createAsyncThunk("spr/queue", async (_, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<QueueResponseType | IResponse> = await axiosApi.get(`${URL_SPR}/${ENDPOINTS_SPR.QUEUE}`);
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// получение аудио файла асинхронного распознавания речи
export const getAudio = createAsyncThunk("spr/audio", async (taskId: string, { rejectWithValue, signal }) => {
	try {
		const response: AxiosResponse<Blob | IResponse> = await axiosApi.get(`${URL_SPR}/${ENDPOINTS_SPR.AUDIO}/${taskId}`, {
			responseType: "blob",
			signal,
		});
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// получение пиков аудио файла асинхронного распознавания речи
export const getAudioPeaks = createAsyncThunk("spr/audioPeaks", async (taskId: string) => {
	const response: AxiosResponse<IResponseAudioPeaks | IResponse> = await axiosApi.get(`${URL_SPR}/${ENDPOINTS_SPR.WAVEFORM}/${taskId}`);
	return response.data;
});

// получение результата асинхронного распознавания речи
export const getAsyncResult = createAsyncThunk("spr/asyncResult", async (taskId: string, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IAsyncModeStatus | IAsyncModeResultMonolog | IAsyncModeResultDialog | IAsyncModeResultFailed> = await axiosApi.get(
			`${URL_SPR}/${ENDPOINTS_SPR.ASYNC_RESULT}/${taskId}`
		);
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// удаление задачи
export const deleteTask = createAsyncThunk("spr/deleteTask", async (taskId: string, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponse> = await axiosApi.delete(`${URL_SPR}/${ENDPOINTS_SPR.QUEUE}/${taskId}`);
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

const sprSlice = createSlice({
	name: "spr",
	initialState,
	reducers: {
		// добавление ссылки на аудио-файл
		addDataUrl: (state, action: PayloadAction<string>) => {
			state.recognition.audioUrl = action.payload;
		},
		// установка async режима
		setAsyncMode: (state, action: PayloadAction<boolean>) => {
			state.recognition.asyncMode = action.payload;
		},
		// изменение временной метки синхронного распознавания
		changeTimestampSync: (state, action: PayloadAction<number>) => {
			state.recognition.timestamp = action.payload;
		},
		// изменение временной метки очереди распознавания
		changeTimestampAsync: (state, action: PayloadAction<number>) => {
			state.asyncMode.timestamp = action.payload;
		},
		// добавление имен спикеров
		addSpeakerList: (state, action: PayloadAction<string[]>) => {
			state.asyncMode.speakerList = action.payload;
		},
		// очистка state
		clearState: (state) => {
			state.recognition = initialState.recognition;
			state.asyncMode = initialState.asyncMode;
			state.deletionTask = initialState.deletionTask;
			state.queue = initialState.queue;
			state.audio = initialState.audio;
		},
		// очистка результата распознавания речи
		clearRecognitionData: (state) => {
			state.recognition = initialState.recognition;
			state.asyncMode.taskId = initialState.asyncMode.taskId;
		},
		// очистка аудио данных
		clearAudio: (state) => {
			state.audio = initialState.audio;
		},
		// очистка результата распознавания асинхронного режима
		clearRecognitionAsyncMode: (state) => {
			state.asyncMode = initialState.asyncMode;
			state.audio = initialState.audio;
		},
		// очистка результата удаления задачи
		clearDeletionTask: (state) => {
			state.deletionTask = initialState.deletionTask;
		},
		// очистка очереди распознавания
		clearQueue: (state) => {
			state.queue = initialState.queue;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(recognizeSpeech.pending, (state) => {
				state.recognition.status = RequestStatus.LOADING;
			})
			.addCase(recognizeSpeech.fulfilled, (state, action) => {
				if (action.payload) {
					state.recognition.status = RequestStatus.IDLE;
					if ("taskID" in action.payload) {
						state.asyncMode.taskId = action.payload.taskID;
					} else {
						// if ('speakers' in action.payload) {
						//   // задаем имена спикерам, если нет и отрезок самого длинного фрагмента для прослушивания
						//   const renamedSpeakers: ISpeakerModified[] = action.payload.speakers.map((speaker, idx) => {
						//     speaker.id = speaker.id || `unknown_${idx}`;
						//     const foundLongestFragment = action.payload && 'speakers' in action.payload && action.payload.splitted.filter(fragment => fragment.speaker === idx).reduce((accumulator, currentValue) => accumulator.duration_ms > currentValue.duration_ms ? accumulator : currentValue);
						//     return {
						//       ...speaker,
						//       startLongestFragment: foundLongestFragment && foundLongestFragment.start_ms,
						//       stopLongestFragment: foundLongestFragment && foundLongestFragment.stop_ms,
						//     } as ISpeakerModified;
						//   });
						//   state.recognition.data = { ...action.payload, speakers: renamedSpeakers };
						// } else state.recognition.data = action.payload;
						state.recognition.data = action.payload;
					}
				}
			})
			.addCase(recognizeSpeech.rejected, (state, action: PayloadAction<unknown>) => {
				state.recognition.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.recognition.error = error;
				state.recognition.message = message;
				if (action.payload instanceof AxiosError && typeof action.payload.response?.data !== "string") {
					state.recognition.data = action.payload.response?.data;
				}
			})
			.addCase(getQueue.pending, (state) => {
				state.queue.status = RequestStatus.LOADING;
			})
			.addCase(getQueue.fulfilled, (state, action) => {
				if (action.payload && typeof action.payload === "object" && !("error" in action.payload)) {
					state.queue.status = RequestStatus.IDLE;
					state.queue.data = action.payload;
				} else state.queue.status = RequestStatus.FAILED;
			})
			.addCase(getQueue.rejected, (state, action: PayloadAction<unknown>) => {
				state.queue.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.queue.error = error;
				state.queue.message = message;
			})
			.addCase(getAsyncResult.pending, (state) => {
				state.asyncMode.requestStatus = RequestStatus.LOADING;
			})
			.addCase(getAsyncResult.fulfilled, (state, action) => {
				// если ошибка
				if (typeof action.payload === "string") {
					state.asyncMode.requestStatus = RequestStatus.FAILED;
					state.asyncMode.taskId = null;
				} else {
					state.asyncMode.requestStatus = RequestStatus.IDLE;
					// если только статус
					if (action.payload.status === "waiting" || action.payload.status === "not found") state.asyncMode.responseStatus = action.payload.status;
					// если готово
					else if (action.payload.status === "ready") {
						// если диалог со спикерами
						if ("speakers" in action.payload) {
							const { status, ...result } = action.payload;
							// задаем имена спикерам, если нет и отрезок самого длинного фрагмента для прослушивания
							const renamedSpeakers = result.speakers.map((speaker, idx) => {
								speaker.id = speaker.id ?? `unknown_${idx}`;
								const speakerModified = {
									...speaker,
									startLongestFragment: 0,
									stopLongestFragment: 0,
								};
								if (action.payload && "speakers" in action.payload && action.payload.splitted.filter((fragment) => fragment.speaker === idx).length > 0) {
									const foundLongestFragment = action.payload.splitted
										.filter((fragment) => fragment.speaker === idx)
										.reduce((accumulator, currentValue) => (accumulator.duration_ms > currentValue.duration_ms ? accumulator : currentValue));
									speakerModified.startLongestFragment = foundLongestFragment.start_ms;
									speakerModified.stopLongestFragment = foundLongestFragment.stop_ms;
								}
								return speakerModified as ISpeakerModified;
							});
							state.asyncMode.data = { ...result, speakers: renamedSpeakers };
							// если монолог
						} else {
							const { status, ...result } = action.payload;
							state.asyncMode.data = { ...result };
						}
						state.asyncMode.responseStatus = null;
						state.asyncMode.taskId = null;
					} else if (action.payload.status === "failed") {
						state.asyncMode.data = action.payload;
						state.asyncMode.responseStatus = null;
						state.asyncMode.taskId = null;
					}
				}
			})
			.addCase(getAsyncResult.rejected, (state, action: PayloadAction<unknown>) => {
				state.asyncMode.requestStatus = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.asyncMode.error = error;
				state.asyncMode.message = message;
			})
			.addCase(getAudio.pending, (state) => {
				state.audio.audioStatus = RequestStatus.LOADING;
			})
			.addCase(getAudio.fulfilled, (state, action) => {
				if (action.payload instanceof Blob && action.payload.type.includes("audio")) {
					state.audio.audioStatus = RequestStatus.IDLE;
					const url = window.URL.createObjectURL(action.payload);
					state.audio.audioUrl = url;
				} else {
					state.audio.audioStatus = RequestStatus.FAILED;
				}
			})
			.addCase(getAudio.rejected, (state, action: PayloadAction<unknown>) => {
				state.audio.audioStatus = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.audio.error = error;
				state.audio.message = message;
			})
			.addCase(getAudioPeaks.pending, (state) => {
				state.audio.audioPeakStatus = RequestStatus.LOADING;
			})
			.addCase(getAudioPeaks.fulfilled, (state, action) => {
				if (action.payload && typeof action.payload === "object" && "waveform" in action.payload && Array.isArray(action.payload.waveform)) {
					state.audio.audioPeakStatus = RequestStatus.IDLE;
					state.audio.audioPeaks = action.payload.waveform;
				} else state.audio.audioPeakStatus = RequestStatus.FAILED;
			})
			.addCase(getAudioPeaks.rejected, (state) => {
				state.audio.audioPeakStatus = RequestStatus.FAILED;
				console.error("Peak loading error. Peaks will be built on the client side.");
			})
			.addCase(deleteTask.pending, (state) => {
				state.deletionTask.status = RequestStatus.LOADING;
			})
			.addCase(deleteTask.fulfilled, (state, action) => {
				state.deletionTask.status = RequestStatus.IDLE;
				if (action.payload) {
					state.deletionTask.error = action.payload.error;
					state.deletionTask.message = action.payload.message;
				}
			})
			.addCase(deleteTask.rejected, (state, action: PayloadAction<unknown>) => {
				state.deletionTask.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.deletionTask.error = error;
				state.deletionTask.message = message;
			});
	},
});

export const {
	addDataUrl,
	setAsyncMode,
	changeTimestampSync,
	changeTimestampAsync,
	addSpeakerList,
	clearState,
	clearRecognitionData,
	clearAudio,
	clearRecognitionAsyncMode,
	clearDeletionTask,
	clearQueue,
} = sprSlice.actions;

export const selectRecognitionData = (state: RootState) => state.spr.recognition;
export const selectRecognitionAsyncData = (state: RootState) => state.spr.asyncMode;
export const selectAudio = (state: RootState) => state.spr.audio;
export const selectDeletionTaskStatus = (state: RootState) => state.spr.deletionTask;
export const selectQueue = (state: RootState) => state.spr.queue;

export default sprSlice.reducer;
